import { useCallback, useMemo } from 'react';
import { useController, useFormContext } from 'react-hook-form';

import {
  checkIf,
  maxFileSizeValidatorFactory,
} from 'components/molecules/reactHookForm/validators';
import useUpdateInstanceFile from 'hooks/useUpdateInstanceFile';

import {
  FieldName,
  FileFieldOnChange,
  FileFieldType,
  HandleUpdateDocumentOnViewer,
} from '../typing';

function useFileInputLogic<TForm>(
  fieldName: FieldName<TForm>,
  localDisabled?: boolean,
  maxFileSizeMB?: number
) {
  // region *******************************DATA*********************************
  const { control, trigger } = useFormContext();

  const { field, fieldState } = useController({
    name: fieldName,
    control,
    rules: {
      validate: {
        sizeLimit: checkIf(!!maxFileSizeMB)(
          maxFileSizeValidatorFactory(maxFileSizeMB ?? 0)
        ),
      },
    },
  });

  const {
    value,
    onChange: _setValue,
    disabled: formDisabled,
  }: {
    value: FileFieldType;
    onChange: FileFieldOnChange;
    disabled?: boolean;
  } = field;

  const setValue = useCallback<FileFieldOnChange>(
    (newValue) => {
      _setValue(newValue);
      trigger(fieldName);
    },
    [_setValue, fieldName, trigger]
  );

  const disabled = useMemo(
    () => localDisabled || formDisabled,
    [formDisabled, localDisabled]
  );

  const [isUpdatingFieldValuePending, startUpdatingFieldValue] =
    useUpdateInstanceFile();

  const { error } = fieldState;

  const fileDisplayStr = useMemo(() => {
    if (!value) return 'No file selected';

    return value instanceof File
      ? value.name
      : value.substring(value.lastIndexOf('/') + 1);
  }, [value]);
  // endregion

  // region *****************************CALLBACKS******************************
  const handleUpdateDocumentOnViewer: HandleUpdateDocumentOnViewer =
    useCallback(
      (blob: Blob) => {
        if (!value) return;
        startUpdatingFieldValue(blob, value as string);
      },
      [startUpdatingFieldValue, value]
    );
  // endregion

  return useMemo(
    () => ({
      disabled,
      error,
      handleUpdateDocumentOnViewer,
      value,
      setValue,
      fileDisplayStr,
      isUpdatingFieldValuePending,
    }),
    [
      disabled,
      error,
      fileDisplayStr,
      handleUpdateDocumentOnViewer,
      isUpdatingFieldValuePending,
      setValue,
      value,
    ]
  );
}

export default useFileInputLogic;
